/**
 * FichaDoPersonagem — o CONTEÚDO da ficha, sem invólucro.
 *
 * Extraído do CharacterSheetEditor em 2026-07-31 (fase 1 de
 * docs/PLANO_MEMBROS_E_FICHA.md). O conteúdo é o mesmo; o que mudou é que ele
 * não sabe mais como está sendo apresentado.
 *
 * Dois invólucros usam este componente:
 *
 * - `CharacterSheetEditor` — `Drawer` que entra pela direita. É o desktop e a
 *   revelação da cerimônia de sorteio: sobreposição, porque são INTERRUPÇÃO.
 * - o modo empilhado do `SidePanel` — a ficha ocupa a aba de Membros, no lugar
 *   da lista. É NAVEGAÇÃO: você foi olhar.
 *
 * **A saída é sempre do invólucro, nunca daqui** (o X do Drawer, o "‹ Membros"
 * do empilhado). É o que permite os dois modos sem um `if` dentro da ficha.
 *
 * Nada de `DrawerTitle`/`DrawerHeader`/`DrawerFooter` aqui: fora de um Drawer
 * o primeiro quebra, e os outros dois são só `div` com classe. O título
 * acessível que o vaul exige é responsabilidade do invólucro.
 */

import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import { PedirTesteDialog } from './PedirTesteDialog'
import { StepperDeFicha } from './StepperDeFicha'
import {
  Heart,
  Zap,
  Star,
  Save,
  Loader2,
  Swords,
  Shield,
  Sparkles, Dices } from 'lucide-react'
import { cn } from '@/lib/utils'
import { apiService } from '@/services/api'
import type { Character, GameAttribute, GameConfig } from '@/types'

export interface FichaDoPersonagemProps {
  character: Character | null
  tableId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (characterId: string, updates: {
    hp_current?: number
    hp_total?: number
    points?: number
    level?: number
    attributes?: Record<string, number>
    name?: string
    class_name?: string
  }) => Promise<void>
  isUpdating?: boolean
  /** Modo somente leitura - apenas visualização, sem edição */
  readOnly?: boolean
  /** Batismo (cerimônia de sorteio): o dono renomeia o personagem na
   *  revelação. Presente só quando a ficha revelada é a do próprio usuário. */
  onRename?: (name: string) => Promise<void>
  /** Mestre pode pedir um teste de atributo a este jogador (fase 3 de
   *  docs/PLANO_TESTE_ATRIBUTO.md). A ação mora aqui, na ficha, e não na lista
   *  de jogadores: a lista é para ler a mesa de relance, não para agir. */
  podePedirTeste?: boolean
  /** O dono desta ficha está na mesa agora? Pedido é momento de mesa: sem ele
   *  presente o servidor recusa, e a recusa tem de aparecer ANTES de o mestre
   *  escolher atributo e dificuldade (pedido do Paulo, 2026-07-26). */
  convocadoOnline?: boolean
}

export function FichaDoPersonagem({
  character,
  tableId,
  open,
  onOpenChange,
  onSave,
  isUpdating = false,
  readOnly = false,
  podePedirTeste = false,
  convocadoOnline = true,
  onRename
}: FichaDoPersonagemProps) {
  // Estados para stats básicos
  const [hpCurrent, setHpCurrent] = useState(0)
  const [hpTotal, setHpTotal] = useState(0)
  const [points, setPoints] = useState(0)
  const [level, setLevel] = useState(1)

  // Batismo (cerimônia): nome em edição pelo dono
  const [renameValue, setRenameValue] = useState('')
  const [isRenaming, setIsRenaming] = useState(false)

  // Identidade — o mestre edita nome e classe direto na ficha
  const [identNome, setIdentNome] = useState('')
  const [identClasse, setIdentClasse] = useState('')

  // Estados para atributos dinâmicos
  const [attributes, setAttributes] = useState<Record<string, number>>({})
  const [gameConfig, setGameConfig] = useState<GameConfig | null>(null)
  const [loadingConfig, setLoadingConfig] = useState(false)
  const [pedindoTeste, setPedindoTeste] = useState(false)

  // Carregar game_config da mesa para obter os atributos
  useEffect(() => {
    if (open && tableId) {
      setLoadingConfig(true)
      apiService.getGameConfig(tableId)
        .then((config) => {
          setGameConfig(config)
        })
        .catch((error) => {
          console.error('Erro ao carregar game_config:', error)
        })
        .finally(() => {
          setLoadingConfig(false)
        })
    }
  }, [open, tableId])

  const aplicarDoPersonagem = useCallback((c: Character) => {
    setHpCurrent(c.hpCurrent || 0)
    setHpTotal(c.hpTotal || 100)
    setPoints(c.points || 0)
    setLevel(c.level || 1)
    setAttributes(c.attributes || {})
    setRenameValue(c.name || '')
    setIdentNome(c.name || '')
    setIdentClasse(c.className || '')
  }, [])

  // Carregar os valores da ficha — duas portas, de propósito (2026-07-31).
  //
  // Desde que o SidePanel passou a guardar o ID em vez do objeto, `character`
  // muda de identidade a cada `character_stats_update` do WebSocket. Isso é o
  // que faz a ficha acompanhar a mesa ao vivo, mas recarregar SEMPRE seria
  // trocar um defeito por outro: o mestre digitando o HP veria o campo voltar
  // sozinho no meio da edição.
  //
  // Porta 1 — abriu, ou trocou de personagem. Sempre carrega.
  const characterId = character?.id
  useEffect(() => {
    if (character && open) aplicarDoPersonagem(character)
    // Depende do ID, não do objeto: a intenção aqui é "abriu / trocou de
    // pessoa". "Chegou dado novo" é a porta 2.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [characterId, open, aplicarDoPersonagem])

  // Porta 2 — dado novo chegou, e a ficha está em leitura. Aí não há edição a
  // proteger: o número na tela deve ser o número da mesa. Em modo de edição
  // quem manda é o formulário, até salvar ou fechar.
  useEffect(() => {
    if (readOnly && character && open) aplicarDoPersonagem(character)
  }, [readOnly, character, open, aplicarDoPersonagem])

  const handleRename = async () => {
    const nome = renameValue.trim()
    if (!onRename || !nome || nome === character?.name) return
    setIsRenaming(true)
    try {
      await onRename(nome)
    } finally {
      setIsRenaming(false)
    }
  }

  const handleSave = async () => {
    if (!character) return

    const updates: {
      hp_current?: number
      hp_total?: number
      points?: number
      level?: number
      attributes?: Record<string, number>
      name?: string
      class_name?: string
    } = {}

    // Identidade (mestre): nome e classe editados direto na ficha
    const nomeTrim = identNome.trim()
    if (!readOnly && nomeTrim && nomeTrim !== character.name) updates.name = nomeTrim
    if (!readOnly && identClasse && identClasse !== character.className) updates.class_name = identClasse

    // Verificar mudanças em stats básicos
    if (hpCurrent !== character.hpCurrent) updates.hp_current = hpCurrent
    if (hpTotal !== character.hpTotal) updates.hp_total = hpTotal
    if (points !== character.points) updates.points = points
    if (level !== character.level) updates.level = level

    // Verificar mudanças em atributos
    const originalAttributes = character.attributes || {}
    const hasAttributeChanges = Object.keys(attributes).some(
      key => attributes[key] !== originalAttributes[key]
    )
    if (hasAttributeChanges) {
      updates.attributes = attributes
    }

    if (Object.keys(updates).length === 0) {
      onOpenChange(false)
      return
    }

    await onSave(character.id, updates)
    onOpenChange(false)
  }

  // Sobrou só para os chips de combate do HP (−10/−5/+5/+10). O ajuste de 1 e
  // o valor tocável moram no StepperDeFicha; os atributos ajustam inline.
  const adjustValue = (
    setter: React.Dispatch<React.SetStateAction<number>>,
    delta: number,
    min = 0,
    max = 9999
  ) => {
    setter(prev => Math.max(min, Math.min(max, prev + delta)))
  }

  if (!character) return null

  const hpPercentage = Math.max(0, Math.min(100, (hpCurrent / hpTotal) * 100))
  const hpColor = hpPercentage > 60 ? 'emerald' : hpPercentage > 30 ? 'amber' : 'rose'

  // Obter lista de atributos do game_config
  const attributesList: GameAttribute[] = gameConfig?.attributes || []

  return (
    /* Coluna própria: quem monta dá a altura (o Drawer via `100dvh`, o modo
       empilhado via `flex-1 min-h-0` do MobileLayout). */
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header com Avatar e Info Básica */}
      <div className="relative flex-shrink-0">
        {/* Background decorativo */}
        <div className="absolute inset-0 bg-gradient-to-b from-violet-600/20 to-transparent h-32" />


        {/* No celular o recuo é de 16px, não 24: são 16px de conteúdo a mais
            por linha, e a linha mais apertada da ficha (os controles de HP)
            cabe ou não por essa margem. A partir de `sm:` volta ao original. */}
        {/* As classes `flex flex-col gap-1.5` vinham do DrawerHeader; repostas na
            mão para o DOM sair idêntico ao de antes da extração. */}
        <div className="flex flex-col gap-1.5 relative p-4 pb-3 sm:p-6 sm:pb-4">
          <div className="flex items-start gap-4">
            {/* Avatar grande */}
            <div className="relative">
              <Avatar className="h-20 w-20 ring-4 ring-violet-500/50 shadow-xl shadow-violet-500/20">
                <AvatarImage src={character.imageUrl} alt={character.name} />
                <AvatarFallback className="bg-violet-600 text-white text-2xl font-bold">
                  {character.name[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              {/* Badge de Nível */}
              <div className="absolute -bottom-1 -right-1 bg-amber-500 text-zinc-900 text-xs font-bold px-2 py-0.5 rounded-full shadow-lg">
                Nv.{level}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 pt-1">
              <h2 className="text-xl text-game-primary dark:text-white mb-1">{character.name}</h2>
              <p className="text-sm text-game-secondary dark:text-zinc-400 flex items-center gap-1.5">
                <Swords className="h-3.5 w-3.5" />
                {character.className}
              </p>
              {character.isNpc && (
                <span className="inline-flex items-center gap-1 mt-2 text-xs bg-violet-500/20 text-violet-600 dark:text-violet-300 px-2 py-0.5 rounded-full">
                  <Shield className="h-3 w-3" />
                  NPC
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo Scrollável */}
      <ScrollArea className="flex-1 min-h-0 overflow-auto">
        {/* O recuo de baixo soma a safe area: sem rodapé (ficha em leitura, e
            a revelação do sorteio) o fim do conteúdo encosta na borda da tela,
            onde o iPhone desenha o home indicator. */}
        <div className="px-4 sm:px-6 space-y-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">

          {/* === BATISMO (cerimônia de sorteio): o dono dá nome ao herói === */}
          {onRename && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
              <p className="mb-2 text-sm font-semibold text-amber-600 dark:text-amber-300">
                ✨ Este herói é seu — batize-o!
              </p>
              <div className="flex items-center gap-2">
                <Input
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void handleRename() }}
                  maxLength={100}
                  placeholder="Nome do personagem"
                  className="bg-white dark:bg-zinc-900 border-slate-200 dark:border-zinc-700 text-game-primary dark:text-white"
                />
                <Button
                  onClick={() => void handleRename()}
                  disabled={isRenaming || !renameValue.trim() || renameValue.trim() === character.name}
                  className="bg-amber-500 font-bold text-zinc-900 hover:bg-amber-400"
                >
                  {isRenaming ? '...' : 'Batizar'}
                </Button>
              </div>
              <p className="mt-2 text-xs text-game-muted dark:text-zinc-400">
                Foto e descrição você ajusta depois, na página da mesa.
              </p>
            </div>
          )}

          {/* === SEÇÃO: IDENTIDADE (mestre edita nome e classe na ficha) === */}
          {!readOnly && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-game-muted dark:text-zinc-500 mb-3 flex items-center gap-2">
                <Swords className="h-3.5 w-3.5" />
                Identidade
              </h3>
              <div className="space-y-3 rounded-xl border border-game-border-primary dark:border-zinc-700/50 bg-game-tertiary dark:bg-zinc-800/50 p-4">
                <div>
                  <span className="mb-1 block text-xs text-game-muted dark:text-zinc-500">Nome</span>
                  <Input
                    value={identNome}
                    onChange={(e) => setIdentNome(e.target.value)}
                    maxLength={100}
                    className="bg-white dark:bg-zinc-900 border-slate-200 dark:border-zinc-700 text-game-primary dark:text-white"
                  />
                </div>
                <div>
                  <span className="mb-1 block text-xs text-game-muted dark:text-zinc-500">Classe</span>
                  {(gameConfig?.classes?.length ?? 0) > 0 ? (
                    <Select value={identClasse} onValueChange={setIdentClasse}>
                      <SelectTrigger className="bg-white dark:bg-zinc-900 border-slate-200 dark:border-zinc-700 text-game-primary dark:text-white">
                        <SelectValue placeholder="Selecione a classe" />
                      </SelectTrigger>
                      <SelectContent>
                        {gameConfig!.classes.map((cls) => (
                          <SelectItem key={cls.id} value={cls.name}>
                            {cls.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="text-xs text-game-muted dark:text-zinc-500">
                      {character.className || 'Sem classes configuradas na mesa'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* === SEÇÃO: STATS VITAIS === */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-game-muted dark:text-zinc-500 mb-3 flex items-center gap-2">
              <Sparkles className="h-3.5 w-3.5" />
              Stats Vitais
            </h3>

            {/* HP Card */}
            <div className="bg-game-tertiary dark:bg-zinc-800/50 rounded-xl p-4 border border-game-border-primary dark:border-zinc-700/50 mb-3">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "p-2 rounded-lg",
                    hpColor === 'emerald' && "bg-emerald-500/20",
                    hpColor === 'amber' && "bg-amber-500/20",
                    hpColor === 'rose' && "bg-rose-500/20"
                  )}>
                    <Heart className={cn(
                      "h-5 w-5",
                      hpColor === 'emerald' && "text-emerald-400",
                      hpColor === 'amber' && "text-amber-400",
                      hpColor === 'rose' && "text-rose-400"
                    )} />
                  </div>
                  <span className="text-sm font-medium text-game-secondary dark:text-zinc-300">Pontos de Vida</span>
                </div>
                <div className="text-right">
                  <span data-testid="ficha-hp-atual" className={cn(
                    "text-2xl font-bold tabular-nums",
                    hpColor === 'emerald' && "text-emerald-400",
                    hpColor === 'amber' && "text-amber-400",
                    hpColor === 'rose' && "text-rose-400"
                  )}>
                    {hpCurrent}
                  </span>
                  <span className="text-game-muted dark:text-zinc-500 text-lg">/{hpTotal}</span>
                </div>
              </div>

              {/* Barra de HP */}
              <div className="h-3 bg-game-quaternary dark:bg-zinc-900 rounded-full overflow-hidden mb-4">
                <div
                  className={cn(
                    "h-full transition-all duration-500 rounded-full",
                    hpColor === 'emerald' && "bg-gradient-to-r from-emerald-600 to-emerald-400",
                    hpColor === 'amber' && "bg-gradient-to-r from-amber-600 to-amber-400",
                    hpColor === 'rose' && "bg-gradient-to-r from-rose-600 to-rose-400"
                  )}
                  style={{ width: `${hpPercentage}%` }}
                />
              </div>

              {/* Controles HP Atual — duas faixas (2026-08-01).
                  A antiga linha de sete botoes (que so cabia com flex-wrap) deu
                  lugar ao stepper de 44px com o valor tocavel; os saltos de
                  combate viram chips largos embaixo, porque combate e o momento
                  em que o HP muda rapido. */}
              {readOnly ? (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-xs text-game-muted dark:text-zinc-500 w-12">Atual</span>
                  <span className="text-game-primary dark:text-white font-bold">{hpCurrent}</span>
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <span className="mb-1.5 block text-xs text-game-muted dark:text-zinc-500">HP atual</span>
                    <StepperDeFicha
                      rotulo="HP atual"
                      value={hpCurrent}
                      onChange={setHpCurrent}
                      min={0}
                      max={hpTotal}
                      corValor={cn(
                        hpColor === 'emerald' && 'text-emerald-500 dark:text-emerald-400',
                        hpColor === 'amber' && 'text-amber-500 dark:text-amber-400',
                        hpColor === 'rose' && 'text-rose-500 dark:text-rose-400'
                      )}
                    />
                    <div className="mt-2 grid grid-cols-4 gap-2">
                      {([-10, -5, 5, 10] as const).map((delta) => (
                        <Button
                          key={delta}
                          type="button"
                          variant="ghost"
                          onClick={() => adjustValue(setHpCurrent, delta, 0, hpTotal)}
                          className={cn(
                            'h-10 rounded-xl border text-sm font-bold',
                            delta < 0
                              ? 'border-rose-500/30 bg-rose-500/5 text-rose-500 hover:bg-rose-500/15 dark:text-rose-400'
                              : 'border-emerald-500/30 bg-emerald-500/5 text-emerald-500 hover:bg-emerald-500/15 dark:text-emerald-400'
                          )}
                        >
                          {delta > 0 ? `+${delta}` : delta}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <span className="mb-1.5 block text-xs text-game-muted dark:text-zinc-500">HP máximo</span>
                    <StepperDeFicha
                      rotulo="HP máximo"
                      value={hpTotal}
                      onChange={setHpTotal}
                      min={1}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Grid: Pontos e Nível */}
            <div className="grid grid-cols-2 gap-3">
              {/* Pontos Card */}
              <div className="bg-game-tertiary dark:bg-zinc-800/50 rounded-xl p-3 border border-game-border-primary dark:border-zinc-700/50">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 rounded-lg bg-cyan-500/20">
                    <Zap className="h-4 w-4 text-cyan-400" />
                  </div>
                  <span className="text-xs font-medium text-game-secondary dark:text-zinc-300">Pontos</span>
                </div>

                <div className="text-center mb-2">
                  <span className="text-2xl font-bold text-cyan-400 tabular-nums">{points}</span>
                </div>

                {!readOnly && (
                  <StepperDeFicha
                    rotulo="Pontos"
                    value={points}
                    onChange={setPoints}
                    min={0}
                    corValor="text-cyan-600 dark:text-cyan-400"
                  />
                )}
              </div>

              {/* Nível Card */}
              <div className="bg-game-tertiary dark:bg-zinc-800/50 rounded-xl p-3 border border-game-border-primary dark:border-zinc-700/50">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-1.5 rounded-lg bg-amber-500/20">
                    <Star className="h-4 w-4 text-amber-400" />
                  </div>
                  <span className="text-xs font-medium text-game-secondary dark:text-zinc-300">Nível</span>
                </div>

                <div className="text-center mb-2">
                  <span className="text-2xl font-bold text-amber-400 tabular-nums">{level}</span>
                </div>

                {!readOnly && (
                  <StepperDeFicha
                    rotulo="Nível"
                    value={level}
                    onChange={setLevel}
                    min={1}
                    corValor="text-amber-600 dark:text-amber-400"
                  />
                )}
              </div>
            </div>
          </div>

          <Separator className="bg-game-border-primary dark:bg-zinc-800" />

          {/* === SEÇÃO: ATRIBUTOS === */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-game-muted dark:text-zinc-500 mb-3 flex items-center gap-2">
              <Shield className="h-3.5 w-3.5" />
              Atributos
            </h3>

            {loadingConfig ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-game-muted dark:text-zinc-500" />
              </div>
            ) : attributesList.length === 0 ? (
              <div className="text-center py-6 text-game-muted dark:text-zinc-500 text-sm">
                Nenhum atributo configurado para esta mesa
              </div>
            ) : (
              // Uma coluna, nome inteiro, stepper de 44px (2026-08-01).
              // Eram duas colunas com o nome truncado ("Raciocínio Ló…") e
              // `−`/`+` de 20px — o pior alvo da tela. A linha inteira agora é
              // do atributo: o nome cabe, e o valor no meio do stepper abre o
              // teclado para digitar direto.
              <div className="space-y-2.5">
                {attributesList.map((attr) => {
                  const value = attributes[attr.id] ?? attr.default ?? 0
                  const percentage = ((value - attr.min) / (attr.max - attr.min)) * 100

                  return (
                    <div
                      key={attr.id}
                      className="bg-game-tertiary dark:bg-zinc-800/50 rounded-xl p-3 border border-game-border-primary dark:border-zinc-700/50"
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-sm font-medium text-game-secondary dark:text-zinc-300" title={attr.name}>
                          {attr.name}
                        </span>
                        {readOnly && (
                          <span className="text-lg font-bold text-violet-500 dark:text-violet-400 tabular-nums">
                            {value}
                          </span>
                        )}
                      </div>

                      {/* Barra de progresso */}
                      <div className="h-1.5 bg-game-quaternary dark:bg-zinc-900 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-violet-600 to-violet-400 rounded-full transition-all duration-300"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>

                      {!readOnly ? (
                        <div className="mt-2.5">
                          <StepperDeFicha
                            rotulo={attr.name}
                            value={value}
                            onChange={(novo) => setAttributes(prev => ({ ...prev, [attr.id]: novo }))}
                            min={attr.min}
                            max={attr.max}
                            dica={`${attr.min}-${attr.max}`}
                            corValor="text-violet-600 dark:text-violet-400"
                          />
                        </div>
                      ) : (
                        <div className="mt-1.5 text-[10px] text-game-muted dark:text-zinc-600 text-right">
                          {attr.min}-{attr.max}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </ScrollArea>

      {/* Pedir teste de atributo a este jogador (só o mestre, e só na ficha
          de outra pessoa).

          O jogador precisa estar na mesa: o pedido abre uma tela para todos e
          só ele pode responder. A recusa aparece AQUI, no botão, e não depois
          de escolher atributo, dificuldade e bônus — descobrir que não podia
          pedir no fim do trabalho é o que o Paulo relatou em 2026-07-26. O
          servidor continua sendo a autoridade (recusa o pedido); esta tela só
          explica no ponto mais cedo possível. Desabilitado em vez de
          escondido: sumir o botão faria o mestre procurar a função perdida. */}
      {podePedirTeste && character && (
        <div className="px-4 pt-3 border-t border-game-border-primary dark:border-zinc-800">
          <Button
            type="button"
            variant="ghost"
            disabled={!convocadoOnline}
            onClick={() => setPedindoTeste(true)}
            data-testid="pedir-teste"
            className="w-full h-11 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Dices className="h-5 w-5 mr-2" />
            Pedir teste de atributo
          </Button>
          {!convocadoOnline && (
            <p
              className="mt-2 text-xs text-game-muted text-center"
              data-testid="pedir-teste-offline"
            >
              {character.name} não está na mesa agora — o pedido precisa
              de quem rola presente.
            </p>
          )}
        </div>
      )}

      <PedirTesteDialog
        open={pedindoTeste}
        onOpenChange={setPedindoTeste}
        tableId={tableId}
        character={character}
      />

      {/* Footer com botão de salvar (apenas para edição) */}
      {!readOnly && (
        <div className="mt-auto flex flex-col gap-2 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-game-border-primary dark:border-zinc-800 bg-game-secondary dark:bg-zinc-900/80 backdrop-blur-sm">
          <Button
            type="button"
            onClick={handleSave}
            disabled={isUpdating}
            className="w-full h-11 bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 text-white font-semibold shadow-lg shadow-violet-500/25"
          >
            {isUpdating ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                Salvar Alterações
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
